import { useMemo, useRef, useState } from 'react'
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback'
import {
  resolveFilterVisitMode,
  useFilterVisitGuard,
  type FilterVisitMode,
} from '@/shared/hooks/useFilterVisitGuard'
import { router, usePage } from '@inertiajs/react'
import { SlidersHorizontal, X } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { DatePickerInput } from '@/shared/ui/date-picker-input'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { MultiSelectCombobox } from '@/shared/ui/multi-select-combobox'
import { Sheet, SheetContent, SheetTrigger, SheetClose } from '@/shared/ui/sheet'
import { t } from '@/shared/lib/i18n'
import {
  buildOrgCascadeChangeHandlers,
  visibleDepartmentOptionsForSites,
  visibleUnitOptionsForParents,
} from '@/shared/lib/orgMultiSelectCascade'
import { complianceDashboardPath } from '@/shared/lib/routes'
import { useHydrateLocationFilters } from '@/shared/hooks/useHydrateLocationFilters'
import { clearLocationFilters, writeLocationFilters } from '@/shared/lib/locationFiltersSession'
import type { SharedProps } from '@/shared/types/SharedProps'

interface ReviewStatusOption {
  id: number
  label: string
}

interface InstrumentOption {
  value: number
  label: string
}

interface SiteOption {
  value: number
  label: string
}

interface DepartmentOption {
  value: number
  label: string
  siteId?: number
}

interface UnitOption {
  value: number
  label: string
  siteId?: number
  departmentId?: number
}

interface ComplianceFiltersShape {
  siteIds?: number[]
  departmentIds?: number[]
  unitIds?: number[]
  // CSV from the page props, or the raw Rack array (`instrument_ids[]=1`) before it is normalized.
  instrumentIds?: string | number | Array<string | number>
  dateFrom?: string
  dateTo?: string
  reviewStatusId?: string
  search?: string
}

// REBUILD-553: the debounced search navigation preserves component state so the search
// input stays mounted and keeps focus across the results refresh. Discrete filter actions
// (org/date/status) leave this false and fully remount as before.
interface NavigateOverrides {
  siteIds?: number[]
  departmentIds?: number[]
  unitIds?: number[]
  instrumentIds?: number[]
  search?: string
  dateFrom?: string
  dateTo?: string
  reviewStatusId?: string
  preserveState?: boolean
}

interface Props {
  filters: ComplianceFiltersShape
  instrumentOptions: InstrumentOption[]
  siteOptions: SiteOption[]
  departmentOptions: DepartmentOption[]
  unitOptions: UnitOption[]
  reviewStatuses: ReviewStatusOption[]
  triggerOnly?: boolean
  desktopOnly?: boolean
}

const ALL_SENTINEL = 'all'
const PENDING_SENTINEL = 'pending'

const parseIds = (value: string | number | Array<string | number> | null | undefined): number[] => {
  if (value == null || value === '') return []

  const parts = Array.isArray(value) ? value : String(value).split(',')
  return parts.map(Number).filter((id) => Number.isInteger(id) && id > 0)
}

const parseOrgIds = (value: number[] | undefined): number[] =>
  (value ?? []).map((id) => Number(id)).filter((id) => id > 0)

const ComplianceFilters = ({
  filters,
  instrumentOptions,
  siteOptions,
  departmentOptions,
  unitOptions,
  reviewStatuses: _reviewStatuses,
  triggerOnly = false,
  desktopOnly = false,
}: Props) => {
  const { currentTenantId, currentUserInfo } = usePage<SharedProps>().props
  const currentUserId = currentUserInfo?.id ?? null

  const sheetOpenRef = useRef(false)
  const { beginVisit } = useFilterVisitGuard()

  const [search, setSearch] = useState(filters.search ?? '')
  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>(parseOrgIds(filters.siteIds))
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<number[]>(
    parseOrgIds(filters.departmentIds),
  )
  const [selectedUnitIds, setSelectedUnitIds] = useState<number[]>(parseOrgIds(filters.unitIds))
  const [selectedInstrumentIds, setSelectedInstrumentIds] = useState<number[]>(
    parseIds(filters.instrumentIds),
  )
  const [dateFrom, setDateFrom] = useState(filters.dateFrom ?? '')
  const [dateTo, setDateTo] = useState(filters.dateTo ?? '')
  const [reviewStatusId, setReviewStatusId] = useState(filters.reviewStatusId ?? PENDING_SENTINEL)

  const navigate = (overrides: NavigateOverrides = {}, mode: FilterVisitMode = 'default') => {
    const sites = overrides.siteIds ?? selectedSiteIds
    const departments = overrides.departmentIds ?? selectedDepartmentIds
    const units = overrides.unitIds ?? selectedUnitIds
    const instruments = overrides.instrumentIds ?? selectedInstrumentIds
    const q = (overrides.search ?? search).trim()
    const df = overrides.dateFrom ?? dateFrom
    const dt = overrides.dateTo ?? dateTo
    const rs = overrides.reviewStatusId ?? reviewStatusId

    writeLocationFilters(currentTenantId, currentUserId, {
      siteIds: sites,
      departmentIds: departments,
      unitIds: units,
    })

    const filterParams: Record<string, string> = {}
    if (q) filterParams.search = q
    if (sites.length > 0) filterParams.site_ids = sites.join(',')
    if (departments.length > 0) filterParams.department_ids = departments.join(',')
    if (units.length > 0) filterParams.unit_ids = units.join(',')
    if (instruments.length > 0) filterParams.instrument_ids = instruments.join(',')
    if (df) filterParams.date_from = df
    if (dt) filterParams.date_to = dt
    // Pending is the server default, so the URL stays clean. All must be sent or the list snaps back to pending.
    if (rs !== PENDING_SENTINEL) filterParams.review_status_id = rs

    // REBUILD-553: preserve component state (and scroll) for the live search so Inertia reuses
    // the mounted page instead of remounting it — the search input keeps focus mid-keystroke
    // across the debounced results refresh. Scope the partial reload to list + pagination.
    const preserveState = overrides.preserveState ?? false
    const visit = beginVisit(
      JSON.stringify(filterParams),
      resolveFilterVisitMode(sheetOpenRef.current, mode),
    )
    if (!visit) return
    const keepDropdownOpen = visit.keepDropdownOpen
    router.get(
      complianceDashboardPath(Object.keys(filterParams).length ? filterParams : {}),
      {},
      {
        preserveState: keepDropdownOpen || preserveState,
        ...(keepDropdownOpen || preserveState ? { preserveScroll: true } : {}),
        ...(preserveState && !keepDropdownOpen ? { only: ['records', 'meta'] } : {}),
      },
    )
  }

  const filteredDepartmentOptions = useMemo(
    () => visibleDepartmentOptionsForSites(departmentOptions ?? [], selectedSiteIds),
    [departmentOptions, selectedSiteIds],
  )

  const filteredUnitOptions = useMemo(
    () => visibleUnitOptionsForParents(unitOptions ?? [], selectedSiteIds, selectedDepartmentIds),
    [unitOptions, selectedSiteIds, selectedDepartmentIds],
  )

  const debouncedNavigate = useDebouncedCallback((overrides: NavigateOverrides) => {
    navigate(overrides)
  }, 400)

  const handleSearchChange = (value: string) => {
    if (value.trim() === '') {
      setSearch('')
      debouncedNavigate.cancel()
      // REBUILD-553: preserveState avoids remounting the page so the search input keeps focus.
      navigate({ search: '', preserveState: true })
      return
    }
    setSearch(value)
    // REBUILD-553: preserveState keeps the input mounted/focused across the debounced refresh.
    debouncedNavigate({ search: value, preserveState: true })
  }

  const orgCascade = buildOrgCascadeChangeHandlers({
    departmentOptions: departmentOptions ?? [],
    unitOptions: unitOptions ?? [],
    selection: {
      siteIds: selectedSiteIds,
      departmentIds: selectedDepartmentIds,
      unitIds: selectedUnitIds,
    },
    applySelection: ({ siteIds, departmentIds, unitIds }) => {
      setSelectedSiteIds(siteIds)
      setSelectedDepartmentIds(departmentIds)
      setSelectedUnitIds(unitIds)
    },
  })

  const closeSheetWithCascadePrune = () => {
    debouncedNavigate.cancel()
    navigate(orgCascade.pruneSelection(), 'sheet-close')
  }

  const handleSiteIdsCommit = (selected: number[]) =>
    navigate(orgCascade.changeSites(selected), 'commit')

  const handleDepartmentIdsCommit = (selected: number[]) =>
    navigate(orgCascade.changeDepartments(selected), 'commit')

  const handleUnitIdsCommit = (selected: number[]) => {
    setSelectedUnitIds(selected)
    navigate({ unitIds: selected }, 'commit')
  }

  const handleDateFromChange = (val: string) => {
    setDateFrom(val)
    navigate({ dateFrom: val })
  }

  const handleDateToChange = (val: string) => {
    setDateTo(val)
    navigate({ dateTo: val })
  }

  const handleReviewStatusChange = (val: string) => {
    setReviewStatusId(val)
    navigate({ reviewStatusId: val })
  }

  const clearFilters = () => {
    debouncedNavigate.cancel()
    setSearch('')
    setSelectedSiteIds([])
    setSelectedDepartmentIds([])
    setSelectedUnitIds([])
    setSelectedInstrumentIds([])
    setDateFrom('')
    setDateTo('')
    setReviewStatusId(PENDING_SENTINEL)
    clearLocationFilters(currentTenantId, currentUserId)
    router.get(complianceDashboardPath(), {}, { preserveState: false })
  }

  useHydrateLocationFilters({
    tenantId: currentTenantId,
    userId: currentUserId,
    // Desktop instance hydrates; mobile trigger-only instance skips (two mounts on same page).
    enabled: !triggerOnly,
    hasUrlLocationParams:
      (filters.siteIds?.length ?? 0) > 0 ||
      (filters.departmentIds?.length ?? 0) > 0 ||
      (filters.unitIds?.length ?? 0) > 0,
    currentFilters: {
      siteIds: parseOrgIds(filters.siteIds),
      departmentIds: parseOrgIds(filters.departmentIds),
      unitIds: parseOrgIds(filters.unitIds),
    },
    siteOptions,
    departmentOptions,
    unitOptions,
    onHydrate: (hydrated) => {
      setSelectedSiteIds(hydrated.siteIds)
      setSelectedDepartmentIds(hydrated.departmentIds)
      setSelectedUnitIds(hydrated.unitIds)
      navigate({
        siteIds: hydrated.siteIds,
        departmentIds: hydrated.departmentIds,
        unitIds: hydrated.unitIds,
      })
    },
  })

  const activeFilterCount = [
    search,
    selectedSiteIds.length > 0,
    selectedDepartmentIds.length > 0,
    selectedUnitIds.length > 0,
    selectedInstrumentIds.length > 0,
    dateFrom,
    dateTo,
    reviewStatusId !== ALL_SENTINEL && reviewStatusId !== PENDING_SENTINEL,
  ].filter(Boolean).length

  const filterFields = (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-start">
      <div className="flex flex-col gap-1 sm:col-span-2 lg:col-auto">
        <Label className="text-xs text-muted-foreground">
          {t('compliance.dashboard.filterSearch')}
        </Label>
        <Input
          placeholder={t('compliance.dashboard.searchPlaceholder')}
          value={search}
          onChange={(event) => handleSearchChange(event.target.value)}
          className="w-full lg:w-64"
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('compliance.dashboard.filterSite')}
        </Label>
        <MultiSelectCombobox
          options={(siteOptions ?? []).map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          selected={selectedSiteIds}
          onChange={orgCascade.changeSites}
          onClose={handleSiteIdsCommit}
          placeholder={t('compliance.dashboard.allSites')}
          className="w-full lg:w-48"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('compliance.dashboard.filterDepartment')}
        </Label>
        <MultiSelectCombobox
          options={filteredDepartmentOptions.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          selected={selectedDepartmentIds}
          onChange={orgCascade.changeDepartments}
          onClose={handleDepartmentIdsCommit}
          placeholder={t('compliance.dashboard.allDepartments')}
          className="w-full lg:w-48"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('compliance.dashboard.filterUnit')}
        </Label>
        <MultiSelectCombobox
          options={filteredUnitOptions.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          selected={selectedUnitIds}
          onChange={setSelectedUnitIds}
          onClose={handleUnitIdsCommit}
          placeholder={t('compliance.dashboard.allUnits')}
          className="w-full lg:w-48"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('compliance.dashboard.filterInstruments')}
        </Label>
        <MultiSelectCombobox
          options={instrumentOptions}
          selected={selectedInstrumentIds}
          onChange={setSelectedInstrumentIds}
          onClose={(selected) => {
            setSelectedInstrumentIds(selected)
            navigate({ instrumentIds: selected }, 'commit')
          }}
          placeholder={t('compliance.dashboard.allInstruments')}
          className="w-full lg:w-48"
          showBadges={false}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('compliance.dashboard.dateFrom')}
        </Label>
        <DatePickerInput
          value={dateFrom}
          onChange={(val) => handleDateFromChange(val)}
          className="w-full lg:w-36"
          allowTyping
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">{t('compliance.dashboard.dateTo')}</Label>
        <DatePickerInput
          value={dateTo}
          onChange={(val) => handleDateToChange(val)}
          className="w-full lg:w-36"
          allowTyping
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t('compliance.dashboard.filterStatus')}
        </Label>
        <Select value={reviewStatusId} onValueChange={handleReviewStatusChange}>
          <SelectTrigger className="w-full lg:w-52 [&>span]:truncate">
            <SelectValue placeholder={t('compliance.dashboard.filterStatusAll')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={PENDING_SENTINEL}>
              {t('compliance.dashboard.filterStatusPending')}
            </SelectItem>
            <SelectItem value="complete">
              {t('compliance.dashboard.filterStatusComplete')}
            </SelectItem>
            <SelectItem value={ALL_SENTINEL}>
              {t('compliance.dashboard.filterStatusAll')}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  )

  return (
    <>
      {/* Mobile: filter trigger button */}
      {!desktopOnly && (
        <Sheet
          onOpenChange={(open) => {
            sheetOpenRef.current = open
            if (open) {
              debouncedNavigate.cancel()
            } else {
              // Prune on close — MultiSelect onClose may not run (e.g. Escape).
              closeSheetWithCascadePrune()
            }
          }}
        >
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              {t('instruments.filtersTitle')}
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-0.5 h-5 min-w-5 px-1 text-xs">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent
            side="bottom"
            className="rounded-t-xl px-4 pt-4 pb-8"
            title={t('instruments.filtersTitle')}
            hideCloseButton
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-semibold">{t('instruments.filtersTitle')}</h2>
              <div className="flex items-center gap-2">
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    onClick={clearFilters}
                  >
                    {t('instruments.filtersClear')}
                  </Button>
                )}
                <SheetClose asChild>
                  <Button variant="ghost" size="icon" aria-label={t('instruments.filtersDone')}>
                    <X className="h-4 w-4" />
                  </Button>
                </SheetClose>
              </div>
            </div>
            <div className="max-h-[60dvh] overflow-y-auto">{filterFields}</div>
          </SheetContent>
        </Sheet>
      )}

      {/* Desktop: inline filter panel */}
      {!triggerOnly && (
        <div className="hidden rounded-md border p-4 lg:block">
          <div className="flex flex-col gap-3">
            {filterFields}
            {activeFilterCount > 0 && (
              <div className="flex items-center lg:justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  onClick={clearFilters}
                >
                  {t('instruments.filtersClear')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default ComplianceFilters
